import { Link } from "react-router-dom";
import {
  ArrowRight,
  Wind,
  Zap,
  Droplets,
  ShowerHead,
  Leaf,
  Wallet,
  Sun,
  Database,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  PhoneCall,
  Calculator as CalcIcon,
  CircleDot,
} from "lucide-react";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";

/* ---------------- Hero ---------------- */
const Hero = () => (
  <section
    data-testid="hwhp-hero"
    className="relative overflow-hidden bg-[#0B132B] text-white"
  >
    {/* Background photo */}
    <div className="absolute inset-0">
      <img
        src="/products/neopower_hero_dusk.png"
        alt="NeoPower Black Shield in a modern home setting at dusk"
        className="w-full h-full object-cover object-center opacity-55"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/85 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-transparent to-[#0B132B]/40" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
      <Reveal>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/90">
          <Leaf className="h-3 w-3 text-[#10B981]" /> Hot Water Heat Pumps · NZ
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h1
          data-testid="hwhp-h1"
          className="font-heading mt-5 text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.04] max-w-4xl"
        >
          Smarter hot water for <br className="hidden sm:block" />
          modern <span className="text-[#10B981]">New Zealand</span> homes.
        </h1>
      </Reveal>
      <Reveal delay={140}>
        <p className="mt-7 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
          Hot water heat pumps use energy more efficiently by drawing warmth from
          the air and transferring it into your water — helping Kiwi households
          reduce running costs and move toward cleaner home energy.
        </p>
      </Reveal>
      <Reveal delay={200}>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link
            to="/free-home-assessment"
            data-testid="hwhp-cta-primary"
            className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30"
          >
            Get a Free Assessment <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/neopower-black-shield"
            data-testid="hwhp-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 text-white px-7 py-3.5 font-semibold transition-all"
          >
            Explore NeoPower Black Shield
          </Link>
        </div>
      </Reveal>

      {/* Floating credibility chips */}
      <Reveal delay={260}>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
          {[
            { v: "Up to 5×", l: "More efficient than electric resistance" },
            { v: "Lower CO₂", l: "Cleaner than gas combustion" },
            { v: "Free Wi-Fi", l: "App-based smart scheduling" },
            { v: "R290", l: "Low-GWP natural refrigerant" },
          ].map((s, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white/8 backdrop-blur border border-white/12 px-4 py-3"
            >
              <p className="font-heading text-base sm:text-lg font-bold text-white leading-none num-tab">
                {s.v}
              </p>
              <p className="mt-1.5 text-[11px] uppercase tracking-[0.14em] text-white/65 leading-snug">
                {s.l}
              </p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>

    {/* Soft bottom fade into next section */}
    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white pointer-events-none" />
  </section>
);

/* ---------------- B. What is a hot water heat pump? ---------------- */
const WhatIsIt = () => (
  <section data-testid="hwhp-what" className="py-20 sm:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <Reveal className="lg:col-span-6">
        <SectionLabel>What is it?</SectionLabel>
        <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
          A smarter way to heat your <span className="text-[#10B981]">home&apos;s hot water.</span>
        </h2>
        <div className="mt-6 space-y-5 text-[#475569] text-lg leading-relaxed max-w-xl">
          <p>
            A hot water heat pump is a high-efficiency appliance that uses
            <span className="text-[#0B132B] font-semibold"> heat pump technology </span>
            to gently move heat from the surrounding air into your water tank — instead of
            burning gas or running a power-hungry electric element.
          </p>
          <p>
            Hot water is one of the largest contributors to household energy use in New
            Zealand, so the system you choose has a real impact on running costs and
            emissions over its lifetime.
          </p>
          <p className="text-[15px] text-[#64748B] italic">
            Source guidance based on EECA&apos;s heat-pump water heater product research.
          </p>
        </div>
      </Reveal>

      <Reveal delay={120} className="lg:col-span-6">
        <div className="relative">
          <div className="absolute -inset-6 rounded-[2.5rem] bg-[radial-gradient(60%_50%_at_70%_30%,rgba(16,185,129,0.20),transparent_60%)] blur-2xl pointer-events-none" />
          <div className="relative rounded-[1.75rem] overflow-hidden ring-1 ring-[#E2E8F0] shadow-[0_30px_80px_-25px_rgba(11,19,43,0.30)]">
            <img
              src="/products/hwhp_smart.jpg"
              alt="NeoPower Black Shield with Smart Control smartphone app showing 48 °C set temperature, schedule and energy saving status"
              className="w-full h-[420px] sm:h-[520px] object-cover object-left"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/75 via-black/35 to-transparent">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-[10px] tracking-[0.2em] uppercase font-semibold text-white">
                <Wind className="h-3 w-3 text-[#10B981]" /> Heat from the air
              </span>
              <p className="mt-2 text-white text-sm sm:text-base font-medium max-w-md">
                Even on cool New Zealand mornings, ambient air contains useful heat — the
                heat pump simply moves it.
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- C. How it works (4-step visual flow) ---------------- */
const STEPS = [
  {
    n: "01",
    icon: Wind,
    title: "Draws warmth from the air",
    body: "A quiet fan pulls in surrounding air through the heat pump unit — even cool ambient air carries useful heat energy.",
  },
  {
    n: "02",
    icon: Zap,
    title: "Boosts &amp; transfers the heat",
    body: "Inverter heat-pump technology compresses a natural refrigerant (R290) to lift the captured warmth to hot-water temperature.",
  },
  {
    n: "03",
    icon: Database,
    title: "Heats &amp; stores water in the tank",
    body: "The transferred heat is delivered into an insulated stainless-steel storage tank — ready for your household demand.",
  },
  {
    n: "04",
    icon: ShowerHead,
    title: "Delivers hot water on demand",
    body: "Plenty of stored hot water flows to your taps, showers, kitchen and laundry — quietly and efficiently.",
  },
];

const HowItWorks = () => (
  <section
    data-testid="hwhp-how"
    className="relative py-20 sm:py-28 bg-[#0B132B] text-white overflow-hidden"
  >
    {/* Soft grid + glow */}
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }}
    />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[480px] w-[680px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <Reveal className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 backdrop-blur border border-white/12 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <Sparkles className="h-3 w-3 text-[#10B981]" /> How it works
          </span>
          <h2 className="font-heading mt-5 text-3xl sm:text-5xl font-bold leading-[1.05] max-w-3xl">
            From outside air to the <span className="text-[#10B981]">hot tap in your home.</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed max-w-2xl">
            Here&apos;s how the NeoPower Black Shield turns free ambient warmth into reliable
            household hot water — in four simple steps.
          </p>
        </Reveal>

        {/* Side image */}
        <Reveal delay={120} className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-5 rounded-[2rem] bg-[radial-gradient(60%_50%_at_50%_50%,rgba(16,185,129,0.18),transparent_60%)] blur-2xl pointer-events-none" />
            <div className="relative rounded-[1.5rem] overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.55)]">
              <img
                src="/products/hwhp_how.jpg"
                alt="NeoPower Black Shield drawing warmth from the air — blue airflow streams entering the heat pump in a cool outdoor setting"
                className="w-full h-[340px] sm:h-[420px] lg:h-[440px] object-cover object-center"
                loading="lazy"
              />
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/12 backdrop-blur-md border border-white/15 text-[10px] tracking-[0.2em] uppercase font-semibold text-white">
                <Wind className="h-3 w-3 text-[#10B981]" /> Air → Hot water
              </span>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <Reveal key={i} delay={i * 80}>
            <div
              data-testid={`hwhp-step-${i + 1}`}
              className="relative h-full rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/10 p-6 hover:border-[#10B981]/40 hover:bg-white/[0.07] transition-all"
            >
              <div className="flex items-start justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/15 text-[#10B981] ring-1 ring-[#10B981]/30">
                  <s.icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="font-heading text-3xl font-bold text-white/15 num-tab leading-none">
                  {s.n}
                </span>
              </div>
              <h3
                className="font-heading mt-5 text-lg font-bold leading-snug"
                dangerouslySetInnerHTML={{ __html: s.title }}
              />
              <p
                className="mt-2 text-sm text-white/70 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: s.body }}
              />

              {/* Flow arrow between cards (desktop) */}
              {i < STEPS.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3.5 -translate-y-1/2 h-7 w-7 items-center justify-center rounded-full bg-[#10B981] text-white shadow-lg shadow-emerald-500/40">
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                </div>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- D. Why it makes sense for NZ homes ---------------- */
const NZ_BENEFITS = [
  {
    icon: Wallet,
    title: "Lower running costs",
    body: "Because a heat pump moves heat rather than generating it, it can be cheaper to run than a traditional electric storage cylinder.",
  },
  {
    icon: Leaf,
    title: "Lower emissions",
    body: "Combined with NZ&apos;s clean electricity mix, electrified hot water meaningfully reduces household carbon emissions vs. gas.",
  },
  {
    icon: Zap,
    title: "Smart electrification upgrade",
    body: "An easy first step in moving your home off gas and onto an all-electric energy footprint.",
  },
  {
    icon: Sun,
    title: "Fits future solar &amp; battery homes",
    body: "Heat-up cycles can be scheduled around your solar window — turning the hot water tank into useful thermal storage.",
  },
  {
    icon: Database,
    title: "Efficient stored hot water",
    body: "An insulated tank holds plenty of hot water, so your system doesn&apos;t need to constantly fire up to keep pace with demand.",
  },
  {
    icon: ShieldCheck,
    title: "Built for regular household demand",
    body: "Well-suited to households with consistent daily hot-water use — performance depends on correct sizing &amp; install.",
  },
];

const WhyNZ = () => (
  <section data-testid="hwhp-why" className="py-20 sm:py-28 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-6 sm:px-8">
      <Reveal className="max-w-3xl">
        <SectionLabel>Why it makes sense for NZ</SectionLabel>
        <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
          Built for the way <span className="text-[#10B981]">Kiwi homes</span> use hot water.
        </h2>
        <p className="mt-5 text-lg text-[#475569] leading-relaxed">
          A modern hot water heat pump aligns well with New Zealand&apos;s energy mix,
          climate and growing focus on home electrification.
        </p>
      </Reveal>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        {NZ_BENEFITS.map((b, i) => (
          <Reveal key={i} delay={i * 60}>
            <div
              data-testid={`hwhp-benefit-${i}`}
              className="group h-full rounded-2xl bg-white border border-[#E2E8F0] p-7 hover:border-[#10B981]/40 hover:shadow-lg hover:shadow-emerald-500/8 hover:-translate-y-0.5 transition-all"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981] ring-1 ring-[#10B981]/20">
                <b.icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <h3
                className="font-heading mt-5 text-lg font-bold text-[#0B132B] leading-snug"
                dangerouslySetInnerHTML={{ __html: b.title }}
              />
              <p
                className="mt-2 text-sm text-[#475569] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: b.body }}
              />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- E. Why AeroMax recommends NeoPower Black Shield ---------------- */
const WhyNeoPower = () => (
  <section data-testid="hwhp-neopower" className="py-20 sm:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <Reveal className="lg:col-span-6 order-2 lg:order-1">
        <SectionLabel>Our recommendation</SectionLabel>
        <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
          Why we recommend the <span className="text-[#10B981]">NeoPower Black Shield.</span>
        </h2>
        <p className="mt-5 text-lg text-[#475569] leading-relaxed max-w-xl">
          When we looked across what&apos;s available in NZ for residential hot water heat
          pumps, the NeoPower Black Shield stood out as a credible, premium option for
          households moving on from older systems.
        </p>

        <ul className="mt-8 space-y-4">
          {[
            { t: "Premium all-in-one design", b: "Compressor, heat exchanger and storage tank combined in a single tidy unit." },
            { t: "Modern matte-black aesthetic", b: "Architectural styling that suits modern New Zealand homes — inside or outside." },
            { t: "Efficient &amp; reliable performance", b: "Inverter compressor with R290 natural refrigerant designed for everyday Kiwi conditions." },
            { t: "Built for Kiwi households", b: "Sized and configured for typical residential hot-water demand profiles." },
            { t: "Strong replacement choice", b: "A solid upgrade pathway for households moving off old gas or electric storage systems." },
          ].map((it, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/25 shrink-0">
                <CheckCircle2 className="h-4 w-4" strokeWidth={2.4} />
              </span>
              <div>
                <p
                  className="font-heading font-bold text-[#0B132B] leading-snug"
                  dangerouslySetInnerHTML={{ __html: it.t }}
                />
                <p className="text-sm text-[#475569] mt-0.5 leading-relaxed">{it.b}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-9">
          <Link
            to="/neopower-black-shield"
            data-testid="hwhp-neopower-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] hover:bg-[#1E293B] text-white px-7 py-3.5 font-semibold transition-all"
          >
            Explore NeoPower Black Shield <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>

      <Reveal delay={120} className="lg:col-span-6 order-1 lg:order-2">
        <div className="relative">
          <div className="absolute -inset-6 rounded-[2.5rem] bg-[radial-gradient(60%_50%_at_70%_30%,rgba(16,185,129,0.20),transparent_60%)] blur-2xl pointer-events-none" />
          <div className="relative rounded-[1.75rem] overflow-hidden ring-1 ring-[#E2E8F0] shadow-[0_30px_80px_-25px_rgba(11,19,43,0.30)] bg-[#0B132B]">
            <img
              src="/products/hwhp_why.jpg"
              alt="NeoPower Black Shield with performance overlays — COP 5.4, water temp 55 °C, 42 dB sound, efficiency graph and smart control panel"
              className="w-full h-[460px] sm:h-[560px] object-cover"
              loading="lazy"
            />
            <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-[10px] tracking-[0.2em] uppercase font-semibold text-white">
              <CircleDot className="h-3 w-3 text-[#10B981]" /> NeoPower Black Shield
            </span>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- F. Final CTA ---------------- */
const FinalCTA = () => (
  <section data-testid="hwhp-final-cta" className="relative py-20 sm:py-24 bg-[#0B132B] text-white overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(50%_70%_at_50%_50%,rgba(16,185,129,0.18),transparent_60%)] pointer-events-none" />
    <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
      <Reveal>
        <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-[1.05]">
          Ready to see if a hot water heat pump <br className="hidden sm:block" />
          <span className="text-[#10B981]">suits your home?</span>
        </h2>
        <p className="mt-5 text-lg text-white/75 leading-relaxed max-w-2xl mx-auto">
          Start with a free assessment from the AeroMax team, or run the numbers yourself
          with our savings calculator.
        </p>
      </Reveal>
      <Reveal delay={120}>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link
            to="/free-home-assessment"
            data-testid="hwhp-final-book"
            className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30"
          >
            <PhoneCall className="h-4 w-4" /> Book Free Assessment
          </Link>
          <Link
            to="/savings-calculator"
            data-testid="hwhp-final-calc"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 text-white px-7 py-3.5 font-semibold transition-all"
          >
            <CalcIcon className="h-4 w-4" /> Calculate Your Savings
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Page ---------------- */
export default function HotWaterHeatPumps() {
  return (
    <div className="bg-white">
      <Hero />
      <WhatIsIt />
      <HowItWorks />
      <WhyNZ />
      <WhyNeoPower />
      <FinalCTA />
    </div>
  );
}
